import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertCircle,
  Ban,
  Building2,
  CheckCircle,
  KeyRound,
  Loader2,
  Mail,
  Network,
  Search,
  Shield,
  Trash2,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import {
  type ApiUser,
  getProfileLabel,
  getProfileLabelFromProfile,
  roleColors,
  roleIcons,
} from '../model';

interface UserCounts {
  total: number;
  admins: number;
  agents: number;
  affiliates: number;
  members: number;
  individuals: number;
}

export function UserStats({
  roleFilter,
  handleRoleFilterChange,
  userCounts,
}: {
  roleFilter: string;
  handleRoleFilterChange: (role: string) => void;
  userCounts: UserCounts;
}) {
  return (
    <>
      {/* Stats - clickable to filter */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-8">
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-primary/50 ${
            !roleFilter ? 'border-primary' : ''
          }`}
          onClick={() => handleRoleFilterChange('')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-xl font-bold">{userCounts.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-purple-500/50 ${
            roleFilter === 'hq_admin' ? 'border-purple-500' : ''
          }`}
          style={{ animationDelay: '50ms' }}
          onClick={() => handleRoleFilterChange('hq_admin')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-500/10 p-2">
                <Shield className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">HQ Admin</p>
                <p className="text-xl font-bold">{userCounts.admins}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-blue-500/50 ${
            roleFilter === 'agent' ? 'border-blue-500' : ''
          }`}
          style={{ animationDelay: '100ms' }}
          onClick={() => handleRoleFilterChange('agent')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-500/10 p-2">
                <UserCheck className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Agents</p>
                <p className="text-xl font-bold">{userCounts.agents}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-green-500/50 ${
            roleFilter === 'affiliate_owner' ? 'border-green-500' : ''
          }`}
          style={{ animationDelay: '150ms' }}
          onClick={() => handleRoleFilterChange('affiliate_owner')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-500/10 p-2">
                <Building2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Affiliate Owners</p>
                <p className="text-xl font-bold">{userCounts.affiliates}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-orange-500/50 ${
            roleFilter === 'member' ? 'border-orange-500' : ''
          }`}
          style={{ animationDelay: '200ms' }}
          onClick={() => handleRoleFilterChange('member')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-500/10 p-2">
                <User className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Members</p>
                <p className="text-xl font-bold">{userCounts.members}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`animate-fade-in cursor-pointer transition-colors hover:border-gray-500/50 ${
            roleFilter === 'individual' ? 'border-gray-400' : ''
          }`}
          style={{ animationDelay: '250ms' }}
          onClick={() => handleRoleFilterChange('individual')}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gray-500/10 p-2">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Individuals</p>
                <p className="text-xl font-bold">{userCounts.individuals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

interface Organization {
  id: string;
  name: string;
}

interface Affiliate {
  id: string;
  name: string;
}

export function UserFilters({
  searchQuery,
  setSearchQuery,
  roleFilter,
  handleRoleFilterChange,
  affiliates,
  gymFilter,
  setGymFilter,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  organizations,
  orgFilter,
  setOrgFilter,
  loadingOrgMembers,
}: {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  roleFilter: string;
  handleRoleFilterChange: (role: string) => void;
  affiliates: Affiliate[];
  gymFilter: string;
  setGymFilter: (value: string) => void;
  sortBy: 'created_at' | 'name';
  setSortBy: Dispatch<SetStateAction<'created_at' | 'name'>>;
  sortOrder: 'asc' | 'desc';
  setSortOrder: Dispatch<SetStateAction<'asc' | 'desc'>>;
  organizations: Organization[];
  orgFilter: string;
  setOrgFilter: (value: string) => void;
  loadingOrgMembers: boolean;
}) {
  return (
    <>
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={[
                  { value: '', label: 'All Roles' },
                  { value: 'hq_admin', label: 'HQ Admin' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'affiliate_owner', label: 'Affiliate Owner' },
                  { value: 'affiliate_coach', label: 'Affiliate Coach' },
                  { value: 'member', label: 'Member' },
                  { value: 'individual', label: 'Individual' },
                ]}
                value={roleFilter}
                onChange={(e) => handleRoleFilterChange(e.target.value)}
              />
            </div>
            {/* Gym Filter */}
            {affiliates.length > 0 && (
              <div className="w-full sm:w-48">
                <Select
                  options={[
                    { value: '', label: 'All Gyms' },
                    ...affiliates.map((a) => ({
                      value: a.id,
                      label: a.name,
                    })),
                  ]}
                  value={gymFilter}
                  onChange={(e) => setGymFilter(e.target.value)}
                />
              </div>
            )}
            {/* Sort Toggle */}
            <Button
              variant="outline"
              size="sm"
              className="whitespace-nowrap"
              onClick={() => {
                if (sortBy === 'name') {
                  setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                } else {
                  setSortBy('name');
                  setSortOrder('asc');
                }
              }}
            >
              {sortBy === 'name' ? (sortOrder === 'asc' ? 'A → Z' : 'Z → A') : 'A-Z'}
            </Button>
            {sortBy === 'name' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSortBy('created_at');
                  setSortOrder('desc');
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Recent
              </Button>
            )}
            {/* Organization Filter */}
            {organizations.length > 0 && (
              <div className="w-full sm:w-56">
                <Select
                  options={[
                    { value: '', label: 'All Organisations' },
                    ...organizations.map((org) => ({
                      value: org.id,
                      label: org.name,
                    })),
                  ]}
                  value={orgFilter}
                  onChange={(e) => setOrgFilter(e.target.value)}
                />
              </div>
            )}
          </div>
          {/* Active org indicator */}
          {orgFilter && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Network className="h-4 w-4" />
              <span>
                Showing users in organization:{' '}
                <span className="font-medium text-foreground">
                  {organizations.find((o) => o.id === orgFilter)?.name}
                </span>
              </span>
              {loadingOrgMembers && <Loader2 className="h-3 w-3 animate-spin" />}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export function UsersTable({
  error,
  fetchUsers,
  filteredUsers,
  users,
  orgFilter,
  isLoading,
  searchQuery,
  roleFilter,
  loadingOrgMembers,
  userOrgMap,
  formatDate,
  handleViewUser,
  handleResendInvite,
  resendingInviteFor,
  handleRequestPasswordReset,
  requestingPasswordResetFor,
  handleSuspendUser,
  handleActivateUser,
  handleDeleteUser,
  hasMore,
  nextCursor,
}: {
  error: string | null;
  fetchUsers: (cursor?: string) => Promise<void>;
  filteredUsers: ApiUser[];
  users: ApiUser[];
  orgFilter: string;
  isLoading: boolean;
  searchQuery: string;
  roleFilter: string;
  loadingOrgMembers: boolean;
  userOrgMap: Map<string, string[]>;
  formatDate: (value: string) => string;
  handleViewUser: (user: ApiUser) => void;
  handleResendInvite: (user: ApiUser) => Promise<void>;
  resendingInviteFor: string | null;
  handleRequestPasswordReset: (user: ApiUser) => Promise<void>;
  requestingPasswordResetFor: string | null;
  handleSuspendUser: (user: ApiUser) => Promise<void>;
  handleActivateUser: (user: ApiUser) => Promise<void>;
  handleDeleteUser: (user: ApiUser) => void;
  hasMore: boolean;
  nextCursor: string | null;
}) {
  return (
    <>
      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => fetchUsers()}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            All Users
            <Badge variant="secondary" className="ml-2">
              {filteredUsers.length} users
              {orgFilter && users.length !== filteredUsers.length && (
                <span className="ml-1 text-muted-foreground">(of {users.length})</span>
              )}
            </Badge>
          </CardTitle>
          <CardDescription>Complete list of registered users in the system</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No users found</h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || roleFilter
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No users have been registered yet.'}
              </p>
            </div>
          ) : (
            <>
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[17%]">User</TableHead>
                    <TableHead className="w-[22%]">Roles</TableHead>
                    <TableHead className="w-[8%] text-center">Status</TableHead>
                    <TableHead className="w-[14%] text-center">Organisation</TableHead>
                    <TableHead className="w-[10%] text-center">Joined</TableHead>
                    <TableHead className="w-[18%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user, index) => {
                    const RoleIcon = roleIcons[user.role] || User;
                    return (
                      <TableRow
                        key={user.id}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="rounded-full bg-primary/10 p-2">
                              <RoleIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {user.profiles && user.profiles.length > 0 ? (
                              user.profiles.map((profile) => (
                                <Badge
                                  key={profile.profile_id}
                                  variant="outline"
                                  className={roleColors[profile.role] || roleColors[user.role]}
                                >
                                  {getProfileLabelFromProfile(profile)}
                                </Badge>
                              ))
                            ) : (
                              <Badge variant="outline" className={roleColors[user.role]}>
                                {getProfileLabel(user)}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {/* Status */}
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={
                              user.invite_status === 'active'
                                ? 'bg-green-500/10 text-green-500 border-green-500/20'
                                : user.invite_status === 'invited'
                                  ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                                  : 'bg-red-500/10 text-red-500 border-red-500/20'
                            }
                          >
                            {user.invite_status === 'active' && 'Active'}
                            {user.invite_status === 'invited' && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                Invited
                              </span>
                            )}
                            {user.invite_status === 'suspended' && 'Suspended'}
                          </Badge>
                        </TableCell>
                        {/* Organization */}
                        <TableCell className="text-center">
                          {loadingOrgMembers ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <div className="flex flex-wrap justify-center gap-1">
                              {(userOrgMap.get(user.id) || []).map((orgName) => (
                                <Badge
                                  key={orgName}
                                  variant="outline"
                                  className="bg-cyan-500/10 text-cyan-500 border-cyan-500/20 text-sm whitespace-nowrap"
                                >
                                  <Network className="h-3 w-3 mr-1 flex-shrink-0" />
                                  {orgName}
                                </Badge>
                              ))}
                              {!userOrgMap.get(user.id)?.length && (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        {/* Joined */}
                        <TableCell className="text-center text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <TooltipProvider delayDuration={100}>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewUser(user)}
                              >
                                View
                              </Button>
                              {user.invite_status === 'invited' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                                      onClick={() => handleResendInvite(user)}
                                      disabled={resendingInviteFor === user.id}
                                    >
                                      {resendingInviteFor === user.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Mail className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Send invitation</TooltipContent>
                                </Tooltip>
                              )}
                              {user.invite_status === 'active' && (
                                <>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                                        onClick={() => handleRequestPasswordReset(user)}
                                        disabled={requestingPasswordResetFor === user.id}
                                      >
                                        {requestingPasswordResetFor === user.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <KeyRound className="h-4 w-4" />
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Password reset</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                        onClick={() => handleSuspendUser(user)}
                                      >
                                        <Ban className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Suspend user</TooltipContent>
                                  </Tooltip>
                                </>
                              )}
                              {user.invite_status === 'suspended' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                      onClick={() => handleActivateUser(user)}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Activate user</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                    onClick={() => handleDeleteUser(user)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete user</TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchUsers(nextCursor || undefined)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
